/**
 * Figure 5, step 1 into step 2: the link opened, before any code is asked for.
 *
 * This screen exists because of one thing, and it is not a technical requirement. **The real failure
 * mode of a passwordless flow is a recipient who cannot tell it from phishing.** Somebody has clicked
 * a link out of their inbox and is being asked to prove they can read that inbox; if the page does
 * not immediately show who is asking and about what, the sensible response is to close the tab. So
 * the rep's name, the exchange title and the masked address are the content, not decoration - they
 * are what `openedGrantSchema` calls "enough to recognise the request as genuine".
 *
 * Opening the link does not consume the grant. Section 08 is explicit: mail security gateways fetch
 * every link in a message before any person clicks it, and consuming here would break the product for
 * every corporate recipient. This screen therefore renders on a `GET` that is deliberately repeatable.
 *
 * The device fast path is the branch not drawn in figure 3. A browser arriving with a trusted device
 * cookie skips the code entirely, so this screen offers "Continue" rather than "Send me a code".
 */
import { useState } from 'react';
import { Button, Card, Page } from '@iqz-ddds/components';
import { FailureNotice } from '../components/FailureNotice';
import type { OpenedGrant } from '@iqz-ddds/contracts';

export interface GrantScreenProps {
  readonly grant: OpenedGrant;
  /** Ask for a code. Resolves once one has been sent. */
  readonly onRequestCode: () => Promise<void>;
  /** Use this browser's trusted device and skip the code. Only offered when one exists. */
  readonly onUseDevice: () => Promise<void>;
}

export function GrantScreen({ grant, onRequestCode, onUseDevice }: GrantScreenProps): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Something went wrong.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page
      eyebrow="Secure documents"
      title={`${grant.repName} has asked you for documents`}
      lede={grant.exchangeTitle}
    >
      <FailureNotice error={error} />

      <Card>
        {grant.deviceTrusted ? (
          <>
            <p>
              You verified this device before, so there is nothing to enter. Continue to see what{' '}
              {grant.repName} needs.
            </p>
            <Button onClick={() => void run(onUseDevice)} disabled={busy}>
              {busy ? 'One moment…' : 'Continue'}
            </Button>
          </>
        ) : (
          <>
            <p>
              To make sure it is you, we will email a six-digit code to{' '}
              {/* Masked by the server. The full address never reaches this surface. */}
              <strong>{grant.maskedEmail}</strong>.
            </p>
            <p className="ddds-lede">
              There is nothing to sign up for and no password to create.
            </p>
            <Button onClick={() => void run(onRequestCode)} disabled={busy}>
              {busy ? 'Sending…' : 'Email me a code'}
            </Button>
          </>
        )}
      </Card>

      <p className="ddds-footer">
        Not expecting this? Close this page and contact {grant.repName} directly rather than replying
        to the email.
      </p>
    </Page>
  );
}
